/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  minTemperature,
  maxTemperature,
  minPressure,
  maxPressure,
  EPSILON,
  HEATCP,
  KELVIN,
  R_AIR,
  T_KAPPA,
  T_KTHETA,
  WV_CA,
  WV_CB,
  WV_CE,
  REFERENCE_PRESSURE,
} from '../Soundings/constants';

export const tempToKelvin = (value: number): number => value + KELVIN;

// Calculate pressure in Pa instead of hPa
export const pressToPascal = (value: number): number => value * 100;

export const bijvoetInterpolationTheta = (
  minRange: number,
  maxRange: number,
) => {
  return (t: number): number => {
    const min = minTemperature + KELVIN;
    const max = maxTemperature + KELVIN;
    const xCoord =
      (Math.sign(t - T_KTHETA) * Math.abs(t - T_KTHETA) ** T_KAPPA -
        Math.sign(min - T_KTHETA) * Math.abs(min - T_KTHETA) ** T_KAPPA) /
      (Math.sign(max - T_KTHETA) * Math.abs(max - T_KTHETA) ** T_KAPPA -
        Math.sign(min - T_KTHETA) * Math.abs(min - T_KTHETA) ** T_KAPPA);
    return minRange * (1 - xCoord) + maxRange * xCoord;
  };
};

export const bijvoetInterpolationPres = (
  minRange: number,
  maxRange: number,
) => {
  return (t: number): number => {
    const yCoord =
      (pressToPascal(t) ** T_KAPPA - pressToPascal(minPressure) ** T_KAPPA) /
      (pressToPascal(maxPressure) ** T_KAPPA -
        pressToPascal(minPressure) ** T_KAPPA);
    return minRange * (1 - yCoord) + maxRange * yCoord;
  };
};

// Returns pressure in hPa
export const pressureFromPosition = (y: number): number => {
  return (
    (y *
      (pressToPascal(maxPressure) ** T_KAPPA -
        pressToPascal(minPressure) ** T_KAPPA) +
      pressToPascal(minPressure) ** T_KAPPA) **
      (1 / T_KAPPA) /
    100
  );
};

const calculateESat = (temperature: number): number => {
  const tempKelvin = temperature - KELVIN;
  return WV_CE * Math.exp((WV_CA * tempKelvin) / (tempKelvin + WV_CB));
};

const latHeat = (temperature: number): number => {
  return (
    (R_AIR / EPSILON) *
    WV_CA *
    WV_CB *
    (temperature / (temperature - KELVIN + WV_CB)) ** 2
  );
};

const dTdp = (pressure: number, temperature: number): number => {
  const eSat = calculateESat(temperature);
  const Rs = (EPSILON * eSat) / (pressure - eSat);
  const Lv = latHeat(temperature);
  const dTdp = (2.0 * temperature) / (7.0 * pressure);

  const dTdp1 = dTdp * (1.0 + (Rs * Lv) / (R_AIR * temperature));
  const dTdp2 =
    dTdp1 /
    (1.0 +
      Rs / (7.0 * EPSILON) +
      ((Lv / temperature) ** 2 * Rs * EPSILON) / (R_AIR * HEATCP));
  return dTdp2;
};

export const calculateThetaFromPressTemp = (
  pressure: number,
  temperature: number,
  basePressure?: number,
): number => {
  const base = basePressure || REFERENCE_PRESSURE;
  const dpPascal = pressure > base ? pressToPascal(-10) : pressToPascal(10); // hPa steps to Pa steps

  const numberOfSteps =
    Math.round((base - pressure) / dpPascal) < 1
      ? 1
      : Math.round((base - pressure) / dpPascal);

  const pstep = (base - pressure) / numberOfSteps;
  let tempN = temperature;
  let pressureN = pressure;

  for (let step = 0; step < numberOfSteps; step += 1) {
    const k1 = pstep * dTdp(pressureN, tempN);
    const k2 = pstep * dTdp(pressureN + 0.5 * pstep, tempN + 0.5 * k1);
    const k3 = pstep * dTdp(pressureN + 0.5 * pstep, tempN + 0.5 * k2);
    const k4 = pstep * dTdp(pressureN + pstep, tempN + k3);
    tempN += k1 / 6.0 + k2 / 3.0 + k3 / 3.0 + k4 / 6.0;
    pressureN += pstep;
  }
  return tempN;
};
